import { ChevronDown, ChevronRight, RefreshCw, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import citriniService from "../../../../services/citriniService";
import { CitriniSyncInfoItem } from "../../../../types/citrini";
import { EmptyState, ErrorBanner, LoadingSpinner } from "../helpers";

interface SkippedContractsTabProps {
  fetchInfoId: number;
  isDarkMode: boolean;
  onCountChange?: (count: number) => void;
}

interface SkippedRow {
  ticker: string;
  compositeFigi: string;
  chinaC1Figi: string;
  chinaC2Figi: string;
  weight: number;
  reason: string;
  parentType: "BASKET" | "PORTFOLIO";
  parentCitriniId: string;
  parentName: string;
  parentProductId: number | null;
}

interface SkippedGroup {
  key: string;
  ticker: string;
  compositeFigi: string;
  chinaC1Figi: string;
  chinaC2Figi: string;
  reason: string;
  sources: {
    parentType: "BASKET" | "PORTFOLIO";
    parentCitriniId: string;
    parentName: string;
    parentProductId: number | null;
    weight: number;
  }[];
}

const REASON_COLORS: Record<string, string> = {
  "Missing compositeFigi": "bg-yellow-100 text-yellow-800",
  "Missing compositeFigi, chinaC1Figi, and chinaC2Figi": "bg-yellow-100 text-yellow-800",
  "Primary exchange is VALUE": "bg-orange-100 text-orange-800",
  "Contract not found in instrument_data": "bg-red-100 text-red-800",
  "Zero weight": "bg-gray-100 text-gray-700",
  "Basket not synced in this run": "bg-yellow-100 text-yellow-800",
};

function buildGroups(rows: SkippedRow[]): SkippedGroup[] {
  const map = new Map<string, SkippedGroup>();
  for (const row of rows) {
    const key = `${row.ticker}||${row.compositeFigi}||${row.chinaC1Figi}||${row.chinaC2Figi}||${row.reason}`;
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        ticker: row.ticker,
        compositeFigi: row.compositeFigi,
        chinaC1Figi: row.chinaC1Figi,
        chinaC2Figi: row.chinaC2Figi,
        reason: row.reason,
        sources: [],
      };
      map.set(key, group);
    }
    group.sources.push({
      parentType: row.parentType,
      parentCitriniId: row.parentCitriniId,
      parentName: row.parentName,
      parentProductId: row.parentProductId,
      weight: row.weight,
    });
  }
  return Array.from(map.values());
}

const SkippedContractsTab: React.FC<SkippedContractsTabProps> = ({
  fetchInfoId,
  isDarkMode,
  onCountChange,
}) => {
  const [rows, setRows] = useState<SkippedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [baskets, portfolios] = await Promise.all([
        citriniService.listBasketsByFetchInfoId(fetchInfoId),
        citriniService.listPortfoliosByFetchInfoId(fetchInfoId),
      ]);
      const names: Record<string, string> = {};
      for (const b of baskets) names[b.citriniId] = b.name;
      for (const p of portfolios) names[p.citriniId] = p.name;

      const allSyncItems: CitriniSyncInfoItem[] = [];
      let page = 1;
      let totalPages = 1;
      while (page <= totalPages) {
        const res = await citriniService.listSyncInfo({
          citriniFetchInfoId: fetchInfoId,
          page,
          pageSize: 100,
          orderDir: "asc",
        });
        allSyncItems.push(...res.items);
        totalPages = res.totalPages;
        page++;
      }

      const compiled: SkippedRow[] = [];
      for (const item of allSyncItems) {
        const d = item.data;
        if (!d) continue;
        const parentName = names[item.citriniId] || item.citriniId;

        if (d.skippedContracts) {
          for (const sc of d.skippedContracts) {
            compiled.push({
              ticker: sc.ticker || sc.tickerId || "-",
              compositeFigi: sc.compositeFigi ?? "null",
              chinaC1Figi: sc.chinaC1Figi ?? "null",
              chinaC2Figi: sc.chinaC2Figi ?? "null",
              weight: sc.weight ?? 0,
              reason: sc.reason,
              parentType: item.productType,
              parentCitriniId: item.citriniId,
              parentName,
              parentProductId: item.productId,
            });
          }
        }
        if (d.skippedBaskets) {
          for (const sb of d.skippedBaskets) {
            compiled.push({
              ticker: sb.tickerId,
              compositeFigi: "null",
              chinaC1Figi: "null",
              chinaC2Figi: "null",
              weight: sb.weight,
              reason: sb.reason,
              parentType: item.productType,
              parentCitriniId: item.citriniId,
              parentName,
              parentProductId: item.productId,
            });
          }
        }
      }

      setRows(compiled);
      onCountChange?.(compiled.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load skipped contracts");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchInfoId]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = React.useMemo(() => {
    const filtered = searchTerm.trim()
      ? rows.filter((r) => {
          const term = searchTerm.toLowerCase();
          return (
            r.ticker.toLowerCase().includes(term) ||
            r.compositeFigi.toLowerCase().includes(term) ||
            r.chinaC1Figi.toLowerCase().includes(term) ||
            r.chinaC2Figi.toLowerCase().includes(term) ||
            r.parentName.toLowerCase().includes(term) ||
            r.parentCitriniId.toLowerCase().includes(term) ||
            r.reason.toLowerCase().includes(term)
          );
        })
      : rows;
    return buildGroups(filtered);
  }, [rows, searchTerm]);

  const toggleGroup = useCallback((key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const totalFiltered = React.useMemo(
    () => groups.reduce((sum, g) => sum + g.sources.length, 0),
    [groups]
  );

  if (loading) {
    return <LoadingSpinner isDarkMode={isDarkMode} message="Compiling skipped contracts..." />;
  }

  if (error) {
    return <ErrorBanner message={error} onRetry={load} isDarkMode={isDarkMode} />;
  }

  if (rows.length === 0) {
    return <EmptyState message="No skipped contracts in this fetch" isDarkMode={isDarkMode} />;
  }

  return (
    <div>
      {/* Search and controls */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            {totalFiltered === rows.length
              ? `${groups.length} unique skipped (${rows.length} total)`
              : `${groups.length} unique (${totalFiltered} of ${rows.length})`}
          </span>
          <div className="relative">
            <Search
              className={`w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 ${
                isDarkMode ? "text-rp-grey/40" : "text-gray-400"
              }`}
            />
            <input
              type="text"
              placeholder="Search ticker, FIGI, name, reason"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`text-xs rounded-lg border pl-8 pr-3 py-1.5 w-64 ${
                isDarkMode
                  ? "bg-rp-surface border-rp-separator text-rp-grey/70 placeholder-rp-grey/40"
                  : "bg-white border-gray-300 text-gray-700 placeholder-gray-400"
              }`}
            />
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className={`px-3 py-1.5 text-xs font-medium rounded-lg border flex items-center ${
            isDarkMode
              ? "bg-rp-surface border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised disabled:opacity-50"
              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className={`text-left text-xs ${
                isDarkMode
                  ? "text-rp-grey/50 border-b border-rp-separator"
                  : "text-gray-500 border-b border-gray-200"
              }`}
            >
              <th className="pb-2 pr-2 font-medium w-8" />
              <th className="pb-2 pr-4 font-medium">Ticker / Ticker ID</th>
              <th className="pb-2 pr-4 font-medium">FIGI</th>
              <th className="pb-2 pr-4 font-medium">Reason</th>
              <th className="pb-2 font-medium text-right">Sources</th>
            </tr>
          </thead>
          <tbody
            className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}
          >
            {groups.map((group) => {
              const isExpanded = expandedGroups.has(group.key);
              return (
                <React.Fragment key={group.key}>
                  <tr
                    onClick={() => toggleGroup(group.key)}
                    className={`cursor-pointer ${
                      isDarkMode ? "hover:bg-rp-surface/50" : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="py-2.5 pr-2">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {group.ticker}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                      }`}
                    >
                      {`${group.compositeFigi},${group.chinaC1Figi},${group.chinaC2Figi}`}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                          REASON_COLORS[group.reason] || "bg-gray-100 text-gray-700"
                        }`}
                      >
                        {group.reason}
                      </span>
                    </td>
                    <td
                      className={`py-2.5 text-right font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                      }`}
                    >
                      {group.sources.length}
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={5} className="p-0">
                        <div
                          className={`px-8 py-3 ${
                            isDarkMode ? "bg-rp-surface/30" : "bg-gray-50"
                          }`}
                        >
                          <table className="w-full text-xs">
                            <thead>
                              <tr
                                className={`text-left ${
                                  isDarkMode
                                    ? "text-rp-grey/40 border-b border-rp-separator"
                                    : "text-gray-400 border-b border-gray-200"
                                }`}
                              >
                                <th className="pb-1.5 pr-3 font-medium">Type</th>
                                <th className="pb-1.5 pr-3 font-medium">Citrini ID</th>
                                <th className="pb-1.5 pr-3 font-medium">Name</th>
                                <th className="pb-1.5 pr-3 font-medium">Product ID</th>
                                <th className="pb-1.5 font-medium text-right">Weight</th>
                              </tr>
                            </thead>
                            <tbody
                              className={`divide-y ${
                                isDarkMode ? "divide-rp-separator/50" : "divide-gray-100"
                              }`}
                            >
                              {group.sources.map((src, i) => (
                                <tr key={i}>
                                  <td className="py-1.5 pr-3">
                                    <span
                                      className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                        src.parentType === "BASKET"
                                          ? "bg-purple-100 text-purple-800"
                                          : "bg-indigo-100 text-indigo-800"
                                      }`}
                                    >
                                      {src.parentType}
                                    </span>
                                  </td>
                                  <td
                                    className={`py-1.5 pr-3 font-mono ${
                                      isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                                    }`}
                                  >
                                    {src.parentCitriniId}
                                  </td>
                                  <td
                                    className={`py-1.5 pr-3 ${
                                      isDarkMode ? "text-rp-grey" : "text-gray-800"
                                    }`}
                                  >
                                    {src.parentName}
                                  </td>
                                  <td
                                    className={`py-1.5 pr-3 font-mono ${
                                      isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                    }`}
                                  >
                                    {src.parentProductId ?? "-"}
                                  </td>
                                  <td
                                    className={`py-1.5 text-right font-mono ${
                                      isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                                    }`}
                                  >
                                    {src.weight}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SkippedContractsTab;
